import { Search, X } from 'lucide-react';
import { useEffect, useEffectEvent, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { LIMITS } from '@sbg/shared/constants';
import { useSuggestions } from '@/lib/catalog';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';
import type { ShopCopy } from './content';

export interface SearchBoxProps {
  /** The committed search text (from the URL). */
  value: string;
  onCommit: (value: string) => void;
  copy: ShopCopy['search'];
}

/**
 * Instant search with suggestions (ARIA combobox). Typing updates the results after a short pause;
 * the drop-down offers stones by name, ↑/↓ move through it, Enter opens the highlighted stone (or
 * searches the text), Esc closes it. The text and the URL stay in step both ways.
 */
export function SearchBox({ value, onCommit, copy }: SearchBoxProps) {
  const navigate = useNavigate();
  const listId = useId();
  const inputId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [text, setText] = useState(value);
  const [seen, setSeen] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  // "Adjust state when a prop changes": a Clear-all or a header search changes the URL under us.
  if (value !== seen) {
    setSeen(value);
    setText(value);
  }

  const debounced = useDebouncedValue(text, 350);
  const commit = useEffectEvent((next: string) => {
    if (next !== value) onCommit(next);
  });
  useEffect(() => {
    commit(debounced.trim());
  }, [debounced]);

  const suggestions = useSuggestions(debounced).data ?? [];
  const expanded = open && text.trim().length >= 2 && suggestions.length > 0;

  const close = (): void => {
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = expanded ? suggestions[active] : undefined;
      if (chosen) {
        close();
        void navigate(`/shop/${chosen.slug}`, { viewTransition: true });
      } else {
        close();
        onCommit(text.trim());
      }
    } else if (event.key === 'Escape') {
      if (expanded) {
        event.preventDefault();
        close();
      }
    }
  };

  return (
    <div
      ref={rootRef}
      className="shop-search"
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget)) close();
      }}
    >
      <label className="sr-only" htmlFor={inputId}>
        {copy.label}
      </label>
      <Search className="shop-search__icon" size={18} aria-hidden="true" />
      <input
        id={inputId}
        className="field shop-search__input"
        type="search"
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
        maxLength={LIMITS.search.max}
        placeholder={copy.placeholder}
        value={text}
        aria-expanded={expanded}
        aria-controls={listId}
        aria-autocomplete="list"
        {...(expanded && active >= 0
          ? { 'aria-activedescendant': `${listId}-${String(active)}` }
          : {})}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {text ? (
        <button
          type="button"
          className="shop-search__clear"
          aria-label={copy.clear}
          onClick={() => {
            setText('');
            onCommit('');
            close();
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
      <ul
        id={listId}
        role="listbox"
        aria-label={copy.suggestions}
        className="shop-search__list"
        hidden={!expanded}
      >
        {suggestions.map((suggestion, index) => (
          // eslint-disable-next-line jsx-a11y-x/click-events-have-key-events -- ARIA 1.2 combobox: options are operated from the input (aria-activedescendant, arrows, Enter); this handler only serves pointer users
          <li
            key={suggestion.slug}
            id={`${listId}-${String(index)}`}
            role="option"
            aria-selected={index === active}
            className="shop-search__option"
            data-active={index === active || undefined}
            onMouseDown={(event) => {
              event.preventDefault(); // keep focus in the input until we navigate
            }}
            onClick={() => {
              close();
              void navigate(`/shop/${suggestion.slug}`, { viewTransition: true });
            }}
          >
            <span className="shop-search__name">{suggestion.name}</span>
            <span className="shop-search__meta">{suggestion.color ?? suggestion.sku}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
